import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { FileText, Download, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: { label: 'Pendente', cls: 'bg-amber-600', icon: Clock },
  approved: { label: 'Aprovado', cls: 'bg-green-600', icon: CheckCircle },
  rejected: { label: 'Rejeitado', cls: 'bg-red-600', icon: XCircle }
};

const AdminEnrollmentRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/enrollment-requests`);
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error('Erro ao carregar solicitações de matrícula.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const downloadDoc = async (reqId, field, filename) => {
    try {
      const res = await axios.get(`${API}/enrollment-requests/${reqId}/documents/${field}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento.');
    }
  };

  const changeStatus = async (reqId, status) => {
    try {
      const fd = new FormData();
      fd.append('status', status);
      await axios.patch(`${API}/enrollment-requests/${reqId}/status`, fd);
      setRequests((rs) => rs.map((r) => (r.id === reqId ? { ...r, status } : r)));
      toast.success('Status atualizado.');
    } catch {
      toast.error('Erro ao atualizar status.');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-red-500" size={32} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-enrollment-requests">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Solicitações de Matrícula (com desconto)</h3>
        <Badge className="bg-zinc-700">{requests.length} solicitação(ões)</Badge>
      </div>

      {requests.length === 0 ? (
        <p className="text-gray-400 text-center py-8">Nenhuma solicitação recebida ainda.</p>
      ) : (
        requests.map((r) => {
          const st = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
          const StIcon = st.icon;
          return (
            <Card key={r.id} className="bg-zinc-900 border-zinc-700">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">{r.full_name}</span>
                      <Badge className={`${st.cls} text-white`}><StIcon size={12} className="mr-1" />{st.label}</Badge>
                    </div>
                    <div className="text-sm text-gray-400 mt-1">
                      {r.email} · {r.whatsapp}
                    </div>
                    <div className="text-sm text-gray-300 mt-1">
                      <strong>{r.bundle_title}</strong> — <span className="text-teal-300">{r.condition_label}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : ''}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => changeStatus(r.id, 'approved')} className="bg-green-600 hover:bg-green-700 h-8">Aprovar</Button>
                    <Button size="sm" onClick={() => changeStatus(r.id, 'rejected')} variant="outline" className="border-red-600 text-red-400 h-8">Rejeitar</Button>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {(r.documents || []).map((doc) => (
                    <button
                      key={doc.field}
                      onClick={() => downloadDoc(r.id, doc.field, doc.original_filename)}
                      className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 rounded-lg px-2.5 py-1.5 text-gray-200 transition-colors"
                      data-testid={`doc-${r.id}-${doc.field}`}
                    >
                      <FileText size={13} className="text-blue-400" />
                      {doc.label}
                      <Download size={12} className="text-gray-400" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default AdminEnrollmentRequests;
